/**
 * @file 转换 object 为 query string 形式
 * @author dafo<huanghouin@baijiahulian.com>
 * @module util/json
 */

/**
 * 将字符串解释为对象
 *
 * @function
 * @param {string} string 要解释的字符串
 * @return {Object} 解释后的对象
 */
export const parse = string => {
    // 首字符为 "{" 或 "[" 时，按 JSON.stringify 后的字符串解释
    if (/^[[{]/u.test(string)) {
        try {
            return new Function(`return (${string})`)();
        }
        catch (e) {
            return {};
        }
    }

    return string.split(/&/u).reduce((json, equalment) => {
        const [key, value] = equalment.split('=');
        if (value != null) {
            json[key] = value;
        }

        return json;
    }, {});
};

/**
 * 处理数据项的值，对于函数类型自动执行
 *
 * @param {*} value 评估的数据值
 * @return {*} 评估处理后的数据值
 */
const evaluate = value => (typeof value === 'function' ? value() : value);

/**
 * 转换对象为 URL 参数
 *
 * @function
 * @param {Object|string} json 要转换的对象
 * @return {string} 转换后的字符串
 */
export const toQuery = (json = '') => (
    typeof json === 'string'
        ? json
        : Object.keys(json)
            .reduce((result, key) => {
                const value = evaluate(json[key]);
                // TODO: 2019 排期，后端处理好不传的 key 为 null 后，可以恢复对空值的一报优化
                if (value != null) {
                    result.push(`${key}=${encodeURIComponent(value)}`);
                }

                return result;
            }, [])
            .join('&')
);

const isObject = input => Object.prototype.toString.call(input) === '[object Object]';

/**
 * 复制目标对象字段到源对象（浅复制）
 *
 * @function
 * @param {Object} source 源对象
 * @param {?Object} target 目标对象
 * @return {Object} 源对象
 */
export const extend = (source, ...args) => {
    const count = args.length;
    if (!count) {
        return source;
    }

    for (const item of args) {

        if (item == null || !isObject(item)) {
            continue;
        }

        Object.keys(item).reduce((result, key) => {
            result[key] = isObject(item[key])
                ? extend({}, result[key], camelToUnder(item[key]))
                : item[key];

            return result;
        }, source);
    }

    return source;
};

/**
 * @constant {RegExp} 判断是否符合 camelcase 的正则
 */
const CAMELCASE_PATTERN = /^[a-z]+([A-Z]+[a-z\d]*)+$/;

/**
 * 将 camelcase 字符转为 underscore 风格
 *
 * @param {string} input 待转换的字符串
 * @return {string} 转换为 underscore 风格的字符
 */
const toUnderScore = input =>
    String(input)
        .replace(/[A-Z]+/g, chr => `_${chr.toLowerCase() + (chr.length > 1 ? '_' : '')}`)
        .replace(/_$/, '');

/**
 * 转换 json 中 key 为 camelcase 风格的项
 *
 * @param {string} key  待转换的键
 * @param {Object} json 待转换的对象
 */
function transform(key, json) {
    const newKey = toUnderScore(key);
    if ((!(newKey in json) || json[newKey] == null) && json[key] != null) {
        json[newKey] = isObject(json[key])
            ? extend({}, json[newKey], camelToUnder(json[key]))
            : json[key];
    }

    if (key in json) {
        delete json[key];
    }

}

/**
 * 转换对象中 key 为 camelcase 的为 underscore 风格
 *
 * @function
 * @param {Object} json 待转换的对象
 * @param {?Array.<string>=} backlist 要转换的名单
 * @return {Object} 转换后的 json 对象
 */
export function camelToUnder(json = {}, backlist) {
    const keys = backlist && backlist.length ? backlist : Object.keys(json);
    keys.filter(key => CAMELCASE_PATTERN.test(key)).forEach(key => transform(key, json));

    return json;
}
